import { Typography } from "@suraasa/placebo-ui"
import clsx from "clsx"
import { useState } from "react"
import { pluralize } from "utils/helpers"

import ImageWithErrorState from "./ImageWithErrorState"

const FaceDetectionIrregularities = ({
  id,
  className,
  noFaceImages,
  multipleFaceImages,
}: {
  id: string
  className?: string
  noFaceImages: string[]
  multipleFaceImages: string[]
}) => {
  const [showTopGradientNoFace, setShowTopGradientNoFace] = useState(false)
  const [showBottomGradientNoFace, setShowBottomGradientNoFace] = useState(true)
  const [showTopGradientMultiple, setShowTopGradientMultiple] = useState(false)
  const [showBottomGradientMultiple, setShowBottomGradientMultiple] =
    useState(true)

  return (
    <div
      id={id}
      className={clsx(
        "rounded-xl border border-surface-200 bg-white p-4",
        className
      )}
      style={{
        boxShadow: "0px 1px 2px 0px #0F172A0D, 0px 4px 8px 0px #0F172A0D",
      }}
    >
      <Typography variant={"title3"} className="mb-2.5">
        Face Detection Irregularities
      </Typography>
      {noFaceImages.length > 0 && (
        <div className="pt-1">
          <div className="mb-3 flex items-center gap-1">
            <Typography variant={"strong"}>No Face Detected</Typography>
            <div className="size-1 rounded-full bg-onSurface-300" />
            <Typography variant={"body"} className="text-critical-500">
              {pluralize("event", noFaceImages.length)}
            </Typography>
          </div>

          <div className="relative">
            <div
              className="grid max-h-[60vh] grid-cols-2 gap-1 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4"
              onScroll={e => {
                const target = e.currentTarget
                const isScrolledFromTop = target.scrollTop > 0
                const isScrolledToBottom =
                  target.scrollHeight - target.scrollTop <=
                  target.clientHeight + 1

                setShowTopGradientNoFace(isScrolledFromTop)
                setShowBottomGradientNoFace(!isScrolledToBottom)
              }}
            >
              {noFaceImages.map((src, index) => (
                <div
                  key={`no-face-${index}`}
                  className="relative bg-gray-200 transition-colors hover:bg-gray-300"
                >
                  <ImageWithErrorState
                    src={src}
                    alt={`No face detected ${index + 1}`}
                    key={index}
                  />

                  <span
                    className="absolute left-0.5 top-0.5 rounded-md bg-white px-1 py-0.25"
                    style={{
                      boxShadow:
                        "0px 1.88px 3.75px 0px #0000000A, 0px 1.25px 1.28px 0px #00000014",
                    }}
                  >
                    <Typography variant="smallBody" className="font-semibold">
                      {index + 1}
                    </Typography>
                  </span>
                </div>
              ))}
            </div>
            {showTopGradientNoFace && (
              <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-white to-transparent" />
            )}
            {showBottomGradientNoFace && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white to-transparent" />
            )}
          </div>
        </div>
      )}
      {multipleFaceImages.length > 0 && (
        <div className="pt-3">
          <div className="mb-3 flex items-center gap-1">
            <Typography variant={"strong"}>Multiple Faces Detected</Typography>
            <div className="size-1 rounded-full bg-onSurface-300" />
            <Typography variant={"body"} className="text-critical-500">
              {pluralize("event", multipleFaceImages.length)}
            </Typography>
          </div>

          <div className="relative">
            <div
              className="grid max-h-[60vh] grid-cols-2 gap-1 overflow-y-auto sm:grid-cols-3 lg:grid-cols-4"
              onScroll={e => {
                const target = e.currentTarget
                const isScrolledFromTop = target.scrollTop > 0
                const isScrolledToBottom =
                  target.scrollHeight - target.scrollTop <=
                  target.clientHeight + 1

                setShowTopGradientMultiple(isScrolledFromTop)
                setShowBottomGradientMultiple(!isScrolledToBottom)
              }}
            >
              {multipleFaceImages.map((src, index) => (
                <div
                  key={`multiple-face-${index}`}
                  className="relative bg-gray-200 transition-colors hover:bg-gray-300"
                >
                  <ImageWithErrorState
                    src={src}
                    alt={`Multiple faces detected ${index + 1}`}
                    key={index}
                  />

                  <span
                    className="absolute left-0.5 top-0.5 rounded-md bg-white px-1 py-0.25"
                    style={{
                      boxShadow:
                        "0px 1.88px 3.75px 0px #0000000A, 0px 1.25px 1.28px 0px #00000014",
                    }}
                  >
                    <Typography variant="smallBody" className="font-semibold">
                      {index + 1}
                    </Typography>
                  </span>
                </div>
              ))}
            </div>
            {showTopGradientMultiple && (
              <div className="pointer-events-none absolute inset-x-0 top-0 h-4 bg-gradient-to-b from-white to-transparent" />
            )}
            {showBottomGradientMultiple && (
              <div className="pointer-events-none absolute inset-x-0 bottom-0 h-4 bg-gradient-to-t from-white to-transparent" />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default FaceDetectionIrregularities
